import { FC, useCallback, useMemo } from 'react';

import { DropdownButton, DropdownOption } from '@dbt-labs/sourdough';

import { formatDateUtcYYYYMMDD } from '@/util/date';

export type DateRange = {
  startDate: string;
  endDate: string;
};

const dateOpts: readonly DropdownOption[] = [
  { label: 'Last 3 Months', value: 90 },
  { label: 'Last 2 Months', value: 60 },
  { label: 'Last 1 Month', value: 30 },
  { label: 'Last 2 Weeks', value: 14 },
  { label: 'Last 1 Week', value: 7 },
] as const;

type DropDownDays = (typeof dateOpts)[number]['label'];

export interface ChartDateDropdownProps {
  defaultOption?: DropDownDays;
  onChange: (dateRange: DateRange) => void;
  testId?: string;
  oldestDate?: Date;
}

// todo: Should it start from begining of the current day?
function daysFromNow(days: number) {
  const d = new Date();
  const dateOffset = 24 * 60 * 60 * 1000 * days;
  d.setTime(d.getTime() - dateOffset);
  return formatDateUtcYYYYMMDD(d);
}
export const DEFAULT_DATE_OPT: DropDownDays = 'Last 2 Weeks';
export const DEFAULT_DATE_RANGE: DateRange = {
  // TODO: startDate should be anchored from endDate below calling `getLastCompletedUtcDate`?
  startDate: daysFromNow(
    Number(dateOpts.find((opt) => opt.label === DEFAULT_DATE_OPT)?.value!),
  ),
  endDate: daysFromNow(0),
};

export const ChartDateDropdown: FC<ChartDateDropdownProps> = ({
  onChange,
  defaultOption: defaultOpt = DEFAULT_DATE_OPT,
  testId = 'chart-date-dropdown',
  oldestDate,
}) => {
  const defaultOption = dateOpts.find((opt) => opt.label === defaultOpt);

  const adjustedDateOpts = useMemo(() => {
    return dateOpts.map((option: DropdownOption) => {
      return {
        ...option,
        disabled: oldestDate
          ? oldestDate >
            new Date(new Date().setDate(new Date().getDate() - Number(option.value)))
          : false,
      };
    });
  }, [oldestDate]);

  const handleDateChange = useCallback(
    (opt: DropdownOption) => {
      const endDate = daysFromNow(0);
      // TODO: startDate should be anchored from endDate?
      const startDate = daysFromNow(parseInt(opt.value.toString()));
      onChange({ startDate, endDate });
    },
    [onChange],
  );

  return (
    <DropdownButton
      className="ml-2 whitespace-nowrap"
      options={adjustedDateOpts}
      defaultOption={defaultOption}
      onChange={handleDateChange}
      testId={testId}
    />
  );
};
